/**
 * Karte „Kunden nach Zahl ihrer Anfos" (Cross-Sell, P4, Plan 2026-10-27 Paket A Nr. 3). Ganze Population, Kunde =
 * Adresse, Euro = Bisher bezahlt netto (echtes Geld). Die Ø-Tage-Spalte macht den Alterseffekt sichtbar: Mehrfachbesteller
 * sind länger dabei und hatten mehr Zeit zu zahlen. „Frühere Zahler" stehen NICHT hier, sondern in P2 (Kundentyp-Karte).
 * Backend: /api/auswertungen/hebel/cross-sell.
 */
import { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { Legende, textAuf, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { BefundZeile, fest, Hinweis, Kachel, KACHEL_RASTER, Laden, int, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import type { CrossSellAntwort, CrossSellZeile } from '../../../helpers/hebelData';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { crossSellBefund } from './befund';
import { Unterzeile } from './teile';

/**
 * P5-Review #8: die Stufen zählen Anfos (ohne Doppelbestellungen, nie verweigerte) — das Backend nennt sie noch
 * „1 Bestellung" usw.; die Anzeige nimmt den Namen aus der Stufe. „Bestellungen" bleibt für „inkl. verweigerter".
 */
const STUFE_NAME: Record<CrossSellZeile['stufe'], string> = { '1': '1 Anfo', '2': '2 Anfos', '3+': '3 und mehr', gesamt: 'Alle Kunden' };
const STUFE_RANG: Record<CrossSellZeile['stufe'], number> = { '1': 1, '2': 2, '3+': 3, gesamt: 4 };

/**
 * Kernaussage (Persona-Umbau A3/A4): Kunden mit 2 und mehr Anfos — ihr Anteil an den Kunden und am bisher bezahlten Umsatz.
 * Aus der Stufe „1 Anfo" gerechnet (Rest zu 100 %); dieselbe Rechnung für Karte und Seitenkopf.
 */
export function mehrfachKern(data: CrossSellAntwort): { kunden: number; kundenAnteil: number | null; umsatzAnteil: number | null } | null {
  const eins = data.stufen.find((x) => x.stufe === '1');
  const s = data.summe;
  if (!eins || s.kunden <= 0) return null;
  const kunden = s.kunden - eins.kunden;
  return {
    kunden,
    kundenAnteil: (kunden / s.kunden) * 100,
    umsatzAnteil: s.umsatz > 0 ? ((s.umsatz - eins.umsatz) / s.umsatz) * 100 : null,
  };
}

/** D16: Standardspalten je Kartenbreite (am Handy Stufe, Kunden, Umsatz). */
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['stufe', 'kunden', 'zahler', 'umsatz', 'je_kunde', 'tage'],
  mittel: ['stufe', 'kunden', 'zahler', 'umsatz', 'je_kunde', 'tage'],
  schmal: ['stufe', 'kunden', 'umsatz'],
};

type Stufe = Exclude<CrossSellZeile['stufe'], 'gesamt'>;
const STUFEN: Stufe[] = ['1', '2', '3+'];

/**
 * Segmente der beiden 100-%-Balken (Audit D12): je Stufe ihr Anteil an den Kunden bzw. am bisher bezahlten Umsatz, in
 * der Reihenfolge 1 / 2 / 3+. Fehlt ein Anteil, zählt er als 0; die Summe bleibt, wie die API sie liefert (≈ 100).
 */
export function anteilSegmente(stufen: CrossSellZeile[], feld: 'anteil' | 'umsatz_anteil'): { stufe: Stufe; anteil: number }[] {
  return STUFEN.map((st) => ({ stufe: st, anteil: Math.max(0, stufen.find((x) => x.stufe === st)?.[feld] ?? 0) }));
}

/** Zwei 100-%-Balken übereinander: Anteil an den Kunden gegen Anteil am Umsatz (eine geordnete Farbe, 1 hell → 3+ kräftig). */
const AnteilBalken: FC<{ stufen: CrossSellZeile[] }> = ({ stufen }) => {
  const viz = useVizStyle();
  const farbe: Record<Stufe, string> = { '1': viz.stufen[0], '2': viz.stufen[2], '3+': viz.stufen[4] };
  const zeilen: { label: string; feld: 'anteil' | 'umsatz_anteil' }[] = [
    { label: 'Anteil an den Kunden', feld: 'anteil' },
    { label: 'Anteil am bisher bezahlten Umsatz', feld: 'umsatz_anteil' },
  ];
  return (
    <Box sx={{ mb: 2 }} data-grafik="kunden-umsatz-anteile">
      <Legende items={STUFEN.map((st) => ({ label: STUFE_NAME[st], color: farbe[st] }))} />
      {zeilen.map((z) => (
        <Box key={z.feld} sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 0.25 }}>{z.label}</Box>
          <Box sx={{ display: 'flex', gap: '2px', height: 22, borderRadius: '4px', overflow: 'hidden' }}>
            {anteilSegmente(stufen, z.feld)
              .filter((sg) => sg.anteil > 0)
              .map((sg) => (
                <Box
                  key={sg.stufe}
                  title={`${STUFE_NAME[sg.stufe]}: ${formatPct(sg.anteil, 0)} ${z.feld === 'anteil' ? 'der Kunden' : 'des bisher bezahlten Umsatzes'}`}
                  sx={{
                    flex: `${sg.anteil} 1 0`,
                    minWidth: 2,
                    backgroundColor: farbe[sg.stufe],
                    color: textAuf(farbe[sg.stufe]),
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: '22px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  {sg.anteil >= 9 ? formatPct(sg.anteil, 0) : ''}
                </Box>
              ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const CrossSellKarte: FC<{ data: CrossSellAntwort | null; fehler: string | null }> = ({ data, fehler }) => {
  const s = data?.summe;
  const alleSpalten: Spalte<CrossSellZeile>[] = [
    {
      key: 'stufe',
      label: TERMS.bestellungenJeKunde.label,
      tip: TERMS.bestellungenJeKunde.tip,
      pflicht: true,
      sortValue: (r) => STUFE_RANG[r.stufe] ?? null,
      render: (r) => <Box sx={{ fontWeight: 600 }}>{STUFE_NAME[r.stufe] ?? r.label}</Box>,
    },
    {
      key: 'kunden',
      label: TERMS.kundenNachBestellungen.short,
      tip: TERMS.kundenNachBestellungen.tip,
      // #31: Kunden-% ist der Anteil an der Spalte, Zahler-% der Anteil in der Zeile — zwei Einheiten, zwei Namen.
      einheit: 'Anzahl · % aller Kunden',
      align: 'right',
      sortValue: (r) => r.kunden,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(r.kunden)}
          <Unterzeile>{formatPct(r.anteil, 0)}</Unterzeile>
        </Box>
      ),
      summary: s ? int(s.kunden) : undefined,
    },
    {
      key: 'zahler',
      label: TERMS.kundenZahler.label,
      tip: TERMS.kundenZahler.tip,
      einheit: 'Anzahl · % der Kunden der Zeile',
      align: 'right',
      // Sortiert nach dem Anteil in der Zeile (die Stufen sind verschieden groß, die Anzahl sagte nur „wie viele Kunden“).
      sortValue: (r) => (r.kunden > 0 ? r.zahler / r.kunden : null),
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(r.zahler)}
          <Unterzeile>{formatPct(r.kunden > 0 ? (r.zahler / r.kunden) * 100 : null, 0)}</Unterzeile>
        </Box>
      ),
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(s.zahler)}
          <Unterzeile>{formatPct(s.kunden > 0 ? (s.zahler / s.kunden) * 100 : null, 0)}</Unterzeile>
        </Box>
      ) : undefined,
    },
    {
      key: 'umsatz',
      label: TERMS.kundenUmsatz.label,
      tip: TERMS.kundenUmsatz.tip,
      einheit: 'netto · Anteil',
      align: 'right',
      sortValue: (r) => r.umsatz,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {fest(tEuro(r.umsatz))}
          <Unterzeile>{formatPct(r.umsatz_anteil, 0)}</Unterzeile>
        </Box>
      ),
      summary: s ? fest(tEuro(s.umsatz)) : undefined,
    },
    {
      key: 'je_kunde',
      label: TERMS.kundenUmsatzJeKunde.short,
      tip: TERMS.kundenUmsatzJeKunde.tip,
      einheit: 'netto',
      align: 'right',
      sortValue: (r) => r.umsatz_je_kunde,
      render: (r) => fest(formatEur(r.umsatz_je_kunde, 0)),
      summary: s ? fest(formatEur(s.umsatz_je_kunde, 0)) : undefined,
    },
    {
      key: 'tage',
      label: TERMS.kundenTageSeitErster.short,
      tip: TERMS.kundenTageSeitErster.tip,
      einheit: 'seit der 1. Anfo',
      align: 'right',
      sortValue: (r) => r.tage_seit_erster,
      render: (r) => int(r.tage_seit_erster === null ? null : Math.round(r.tage_seit_erster)),
      summary: s ? int(s.tage_seit_erster === null ? null : Math.round(s.tage_seit_erster)) : undefined,
    },
  ];
  const meta = spaltenMeta(alleSpalten);
  const wahl = useSpaltenwahl('hebelCrossSell', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = alleSpalten.filter((c) => sichtbar.has(c.key));

  return (
    <ChartCard
      title="Kunden nach Zahl ihrer Anfos"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          Wie viele Kunden eine, zwei oder mehr Anfos haben und welchen Teil des bisher bezahlten Umsatzes sie tragen.
          Mehrfachbesteller sind im Schnitt länger dabei (letzte Spalte) und hatten mehr Zeit zu bestellen und zu zahlen — der
          höhere Betrag je Kunde ist deshalb zum Teil Alter, nicht nur Treue.
        </>
      }
      terms={[
        { label: TERMS.kundenNachBestellungen.label, tip: TERMS.kundenNachBestellungen.tip },
        { label: TERMS.kundenUmsatz.label, tip: TERMS.kundenUmsatz.tip },
        { label: TERMS.kundenMehrprodukt.label, tip: TERMS.kundenMehrprodukt.tip },
      ]}
      source="PL-X-Bestellungen und Zahlungen, alle Anfos seit Beginn der Daten"
    >
      <Box ref={wahl.messRef}>
      {!data || !s ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {(() => {
            const b = crossSellBefund(data.stufen);
            return <BefundZeile text={'text' in b ? b.text : undefined} fehlt={'fehlt' in b ? b.fehlt : null} />;
          })()}
          {(() => {
            // A3: „X % der Kunden (2 und mehr Anfos) bringen Y % des bezahlten Umsatzes" — Y ist der Hauptwert; die frühere
            // Kachel „mindestens zwei Produkte" steht als Unterzeile.
            const k = mehrfachKern(data);
            return (
              <Box sx={KACHEL_RASTER}>
                <Kachel
                  wert={fest(formatPct(k?.umsatzAnteil ?? null, 0))}
                  label={TERMS.kundenMehrfachUmsatz.label}
                  tip={`${TERMS.kundenMehrfachUmsatz.tip} ${TERMS.kundenMehrprodukt.label}: ${TERMS.kundenMehrprodukt.tip}`}
                  sub={
                    <>
                      {fest(formatPct(k?.kundenAnteil ?? null, 0))} der Kunden ({int(k?.kunden ?? null)} von {int(s.kunden)}) bringen{' '}
                      {fest(formatPct(k?.umsatzAnteil ?? null, 0))} des bisher bezahlten Umsatzes · {fest(formatPct(data.mehrprodukt_anteil, 0))}{' '}
                      haben mindestens zwei Produkte
                    </>
                  }
                />
              </Box>
            );
          })()}
          <AnteilBalken stufen={data.stufen} />
          <SortableTable rows={data.stufen} columns={spalten} rowKey={(r) => r.stufe} initialSort={{ key: 'stufe', dir: 'asc' }} summaryLabel="Alle Kunden" maxHeight={null} festeErsteSpalte eng />
          <Hinweis>
            Euro netto, bisher bezahlt nach heutigem Stand. Gezählt: {int(s.bestellungen)} Anfos (ohne Doppelbestellungen; verweigerte
            Bestellungen zählen nie); nicht als eigene Anfo gezählt {int(data.nicht_gezaehlt.doppel)} Doppelbestellungen
            {data.nicht_gezaehlt.upsell_nachbar > 0 &&
              ` und ${int(data.nicht_gezaehlt.upsell_nachbar)} Jahresbezug-Orders neben einer anderen Anfo (binnen ${data.sitzung_stunden} Stunden)`}
            ; ihr Umsatz zählt mit. Eine Jahresbezug-Order allein zählt als Anfo — PL-X ersetzt mit ihr die erste Bestellung.{' '}
            {data.ohne_email > 0 && `${int(data.ohne_email)} Anfos ohne E-Mail stehen als eigene Kunden bei „${STUFE_NAME['1']}“. `}
            {data.vorlaeufig > 0 && `${int(data.vorlaeufig)} vorläufig ergänzte Anfos fehlen (ohne Adresse und Zahlstand). `}
            Wer vorher schon gezahlt hat und wie diese Kunden heute zahlen, zeigt{' '}
            <Link component={RouterLink} to="/auswertungen/umsatz#wer-bestellt-wer-zahlt" underline="hover">
              Umsatz &amp; Bezahlqualität → Wer bestellt — und wer zahlt
            </Link>
            .
          </Hinweis>
        </>
      )}
      </Box>
    </ChartCard>
  );
};

export default CrossSellKarte;
